'use client';

import '~/styles/globals.css';

import React, { Suspense, useEffect } from 'react';
import { AppToaster } from '~/components/AppToaster';
import PostHogProvider from '~/components/Providers/PosthogProvider';
import usePageTitle from '~/hooks/usePageTitle';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { Auth0Provider } from '@auth0/nextjs-auth0/client';
import { RouteHistoryProvider } from '~/components/Providers/RouteHistoryProvider';
import { WorkspaceProvider } from '~/contexts/WorkspaceContext';
import { useParams } from 'next/navigation';
import { localStorageKeys, useLocalStorage } from '~/hooks/useLocalStorage';
import ContentPaneLoader from '~/components/Loading/ContentPaneLoader';

function ClientLayout({ children }: { children: React.ReactNode }) {
  usePageTitle(null);

  const params = useParams();
  const [, setStoredWorkspaceId] = useLocalStorage(localStorageKeys.currentWorkspaceId, null);
  const workspaceId = params?.workspaceId as string | undefined;
  const isInWorkspaceRoute = !!workspaceId && typeof workspaceId === 'string';

  // Fetch workspace details if in workspace route
  const { data: workspaceData, isLoading: isWorkspaceLoading } =
    api.workspace.getUserWorkspaces.useQuery(undefined, {
      enabled: isInWorkspaceRoute,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: serverError,
    });

  const currentWorkspace = workspaceData?.find((w) => w.workspaceId === workspaceId);
  const defaultWorkspace = workspaceData?.find((w) => w.isDefault) ?? workspaceData?.[0] ?? null;

  useEffect(() => {
    if (currentWorkspace) {
      setStoredWorkspaceId(currentWorkspace.workspaceId);
    }
  }, [currentWorkspace, setStoredWorkspaceId]);

  return (
    <Suspense fallback={<ContentPaneLoader />}>
      <RouteHistoryProvider>
        <Auth0Provider>
          <WorkspaceProvider
            workspaceId={workspaceId!}
            workspaceName={currentWorkspace?.workspace.name ?? ''}
            isLoading={isWorkspaceLoading}
            hasWorkspaceAccess={!!currentWorkspace}
            defaultWorkspaceId={defaultWorkspace?.workspaceId ?? null}
          >
            <PostHogProvider>{children}</PostHogProvider>
          </WorkspaceProvider>
        </Auth0Provider>
      </RouteHistoryProvider>
      <AppToaster />
    </Suspense>
  );
}

export default api.withTRPC(ClientLayout) as typeof ClientLayout;
